import React, { useState } from "react";
import PaymentApiService from "../../ApiServices/PaymentApiServices/PaymentApiService";
import { useAuth } from '../Auth/AuthContext';
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { motion, AnimatePresence } from "framer-motion";

// Icons
import {
  CurrencyDollarIcon,
  EnvelopeIcon,
  CreditCardIcon,
  BanknotesIcon
} from "@heroicons/react/24/outline";

const InputField = ({ icon: Icon, ...props }) => (
  <div className="relative">
    <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">
      <Icon className="h-5 w-5" />
    </span>
    <input
      {...props}
      className="w-full pl-10 pr-4 py-2 border rounded focus:ring-2 focus:ring-blue-500 outline-none"
    />
  </div>
);

const PaymentForm = ({ onSuccess }) => {
  const [method, setMethod] = useState("");
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    btcAddress: "",
    paypalEmail: "",
    payoneerEmail: "",
    bankName: "",
    accountName: "",
    accountNumber: "",
    swiftCode: ""
  });

  const Auth = useAuth();
  const user = Auth.authState.user;

  const validateForm = () => {
    switch (method) {
      case "BTC":
        return form.btcAddress.trim() !== "";
      case "PayPal":
        return form.paypalEmail.trim() !== "";
      case "Payoneer":
        return form.payoneerEmail.trim() !== "";
      case "Bank":
        return (
          form.bankName.trim() &&
          form.accountName.trim() &&
          form.accountNumber.trim() &&
          form.swiftCode.trim()
        );
      default:
        return false;
    }
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error("Please fill in all required fields for the selected method.");
      return;
    }

    const data = {
      method,
      BTCAddress: form.btcAddress,
      PayPalEmail: form.paypalEmail,
      PayoneerEmail: form.payoneerEmail,
      BankName: form.bankName,
      AccountName: form.accountName,
      AccountNumber: form.accountNumber,
      SwiftCode: form.swiftCode,
      PublisherId: user.id
    };

    try {
      setLoading(true);
      await PaymentApiService.Add(data);
      setLoading(false);
      toast.success("Account information updated!");
      if (onSuccess) onSuccess();
    } catch (error) {
      toast.error("Failed to save payment info");
      console.error(error);
    } finally {
      setMethod("");
      setForm({
        btcAddress: "",
        paypalEmail: "",
        payoneerEmail: "",
        bankName: "",
        accountName: "",
        accountNumber: "",
        swiftCode: ""
      });
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="max-w-xl mx-auto p-6 bg-white shadow-lg rounded-xl space-y-6"
    >
      <h2 className="text-2xl font-bold text-blue-700 text-center">💰 Add Payment Information</h2>

      <select
        className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500"
        value={method}
        onChange={(e) => setMethod(e.target.value)}
      >
        <option value="">🧾 Select Payment Method</option>
        <option value="BTC">₿ BTC</option>
        <option value="PayPal">📧 PayPal</option>
        <option value="Payoneer">💳 Payoneer</option>
        <option value="Bank">🏦 Bank Transfer</option>
      </select>

      <AnimatePresence mode="wait">
        {method === "BTC" && (
          <motion.div
            key="btc"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
          >
            <InputField
              icon={CurrencyDollarIcon}
              type="text"
              name="btcAddress"
              placeholder="BTC Address"
              value={form.btcAddress}
              onChange={handleChange}
            />
          </motion.div>
        )}

        {method === "PayPal" && (
          <motion.div
            key="paypal"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
          >
            <InputField
              icon={EnvelopeIcon}
              type="email"
              name="paypalEmail"
              placeholder="PayPal Email"
              value={form.paypalEmail}
              onChange={handleChange}
            />
          </motion.div>
        )}

        {method === "Payoneer" && (
          <motion.div
            key="payoneer"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
          >
            <InputField
              icon={CreditCardIcon}
              type="email"
              name="payoneerEmail"
              placeholder="Payoneer Email"
              value={form.payoneerEmail}
              onChange={handleChange}
            />
          </motion.div>
        )}

        {method === "Bank" && (
          <motion.div
            key="bank"
            className="space-y-4"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
          >
            <InputField
              icon={BanknotesIcon}
              type="text"
              name="bankName"
              placeholder="Bank Name"
              value={form.bankName}
              onChange={handleChange}
            />
            <InputField
              icon={BanknotesIcon}
              type="text"
              name="accountName"
              placeholder="Account Holder Name"
              value={form.accountName}
              onChange={handleChange}
            />
            <InputField
              icon={BanknotesIcon}
              type="text"
              name="accountNumber"
              placeholder="Account Number"
              value={form.accountNumber}
              onChange={handleChange}
            />
            <InputField
              icon={BanknotesIcon}
              type="text"
              name="swiftCode"
              placeholder="SWIFT Code"
              value={form.swiftCode}
              onChange={handleChange}
            />
          </motion.div>
        )}
      </AnimatePresence>

      <button
        type="submit"
        disabled={loading}
        className="w-full bg-blue-600 text-white font-medium py-2 rounded hover:bg-blue-700 transition"
      >
        {loading ? "💾 Saving..." : "✅ Save Payment Info"}
      </button>
    </form>
  );
};

export default PaymentForm;
